import { useMemo } from 'react';

import {
  coerceSizeValue,
  coerceSizeValueToPixels,
} from '../../../core/utils/coerceSizeValue.js';
import { DEFAULT_CHART_HEIGHT, FRAME_HEIGHT_PX } from '../constants.js';

type UseFlameHeightResult = {
  /** Pixel height to pass to XYChart. Equals the full content height when auto or scrolling; the viewport height otherwise. */
  computedHeight: number;
  /** True when the content is taller than the viewport and a scroll container is needed. */
  shouldScroll: boolean;
  /** CSS height string for the scroll wrapper element (e.g. `"300px"`). Falls back to `DEFAULT_CHART_HEIGHT` when `height` is undefined. */
  scrollSectionHeight: string;
};

/**
 * Resolves the effective pixel height for a flame visualization and whether its
 * content should scroll. When `height` is `'auto'` or the content is taller than
 * the viewport, the chart grows to fit all depth levels. Otherwise it is clamped
 * to the given viewport height.
 *
 * @internal
 */
export const useFlameHeight = (
  height: number | string | undefined,
  depthCount: number,
  overheadPx: number,
): UseFlameHeightResult => {
  const isAuto = height === 'auto';
  const contentHeight = depthCount * FRAME_HEIGHT_PX + overheadPx;
  const viewportPx = coerceSizeValueToPixels(height);
  const effectiveViewportPx = viewportPx ?? DEFAULT_CHART_HEIGHT;
  const shouldScroll = !isAuto && contentHeight > effectiveViewportPx;

  const computedHeight = useMemo(
    () => (isAuto || shouldScroll ? contentHeight : effectiveViewportPx),
    [isAuto, shouldScroll, contentHeight, effectiveViewportPx],
  );

  const scrollSectionHeight =
    coerceSizeValue(height) ?? `${DEFAULT_CHART_HEIGHT}px`;

  return { computedHeight, shouldScroll, scrollSectionHeight };
};
